import { useTranslation } from 'react-i18next';
import type { IApiErrorPlain, IWorkspaceMember } from '@platform/core/types';
import { useConfirm } from '@platform/engine/form/confirm/useConfirm';
import { useChangeMemberRoleMutation, useMeQuery, useMembersQuery, useRemoveMemberMutation } from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IMemberActions } from './IMemberActions';

/** Сервер без цих дій: маршрут не знайдено (404 без ProblemDetails) чи метод не дозволено (405). */
const unsupported = (e: IApiErrorPlain) => e.status === 405 || (e.status === 404 && e.code === 'http_404');

/**
 * Керування учасниками (прототип v4 «Учасники»: роль і «Вилучити»): потрібна можливість members; власника змінює й вилучає лише власник;
 * єдиного власника не можна понизити чи вилучити — такі рядки лише для читання з поясненням. Вилучення підтверджується; помилки сервера
 * (зокрема сервер без цих дій) — повідомленням.
 */
export function useMemberActions(ws: string): IMemberActions {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: me } = useMeQuery();
  const { data: members } = useMembersQuery(ws, { skip: !ws });
  const [patch] = useChangeMemberRoleMutation();
  const [del] = useRemoveMemberMutation();
  const { confirm, dialog } = useConfirm();
  const mine = me?.workspaces.find((w) => w.id === ws);
  const owners = members?.filter((m) => m.role === 'Owner').length ?? 0;
  const fail = (e: IApiErrorPlain) => dispatch(toastShown({ kind: 'error', text: unsupported(e) ? t('membersPage.manageUnavailable') : e.message }));
  const reasonOf = (m: IWorkspaceMember): string | null => {
    if (!mine?.myCapabilities.includes('members')) return t('membersPage.noRight');
    if (m.role === 'Owner' && mine.myRole !== 'Owner') return t('membersPage.ownerOnly');
    if (m.role === 'Owner' && owners <= 1) return t('membersPage.soleOwner');
    return null;
  };
  return {
    reasonOf, dialog,
    changeRole: (m, role) => { if (role !== m.role) void patch({ ws, userId: m.user.id, role }).unwrap().catch(fail); },
    remove: (m) => void confirm({ title: t('membersPage.removeTitle'), text: t('membersPage.removeText', { name: m.user.name }), confirmLabel: t('membersPage.remove'), danger: true })
      .then((ok) => { if (ok) void del({ ws, userId: m.user.id }).unwrap().catch(fail); }),
  };
}
